/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { ink } from "../lib/ink";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { toast } from "../components/ui/sonner";
import { AlertTriangle, TrendingUp, TrendingDown, RefreshCw, Send, Users } from "lucide-react";

const REGION_COLOR = { la: "#3B57F0", norcal: "#6a4be0", sd: "#b45309" };

const KpiCard = ({ label, value, tint, sub }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`overdue-kpi-${label}`}>
    <p className="text-xs text-slate-400">{label}</p>
    <p className="text-2xl font-bold mt-1" style={{ color: tint || "#0f172a" }}>{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

export default function CRMOverdue() {
  const { lang } = useApp();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [batching, setBatching] = useState(false);
  const zh = lang === "zh";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crm/overdue-board");
      setData(data);
    } catch (e) {
      toast.error(zh ? "加载超期看板失败" : "Failed to load");
    } finally { setLoading(false); }
  }, [zh]);

  useEffect(() => { load(); }, [load]);

  const batchNudgeAll = async () => {
    setBatching(true);
    try {
      const { data } = await api.post("/crm/overdue-leads/batch-nudge", {});
      toast.success(zh ? `已催办 ${data.nudged} 户，跳过 ${data.skipped} 户（冷静期内）` : `Nudged ${data.nudged}, skipped ${data.skipped}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "批量催办失败" : "Batch nudge failed"));
    } finally { setBatching(false); }
  };

  // build a merged 7-day chart series across regions
  const buildChart = () => {
    if (!data) return [];
    const len = (data.overall.trend7 || []).length;
    const days = Array.from({ length: len }, (_, i) => `D${i - len + 1 === 0 ? "0" : i - len + 1}`);
    return days.map((d, i) => {
      const row = { day: i === len - 1 ? (zh ? "今日" : "Today") : `-${len - 1 - i}d` };
      (data.regions || []).forEach((r) => { row[r.zh] = (r.trend7 || [])[i]; });
      row[zh ? "全部" : "All"] = (data.overall.trend7 || [])[i];
      return row;
    });
  };

  if (loading) return <div className="p-8 text-slate-400">{zh ? "加载中…" : "Loading…"}</div>;
  if (!data) return null;
  const chart = buildChart();
  const showAll = data.scope === "all";

  return (
    <div className="space-y-6" data-testid="crm-overdue-board">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex-wrap md:flex-nowrap flex items-center gap-2">
          <AlertTriangle className="h-6 w-6 text-red-600" />
          <h1 className="text-2xl font-bold text-slate-900">{zh ? "超期客户看板" : "Overdue Leads Board"}</h1>
        </div>
        <div className="flex-wrap md:flex-nowrap flex items-center gap-2">
          <button data-testid="overdue-refresh" onClick={load} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
            <RefreshCw className="h-4 w-4" />{zh ? "刷新" : "Refresh"}
          </button>
          <button data-testid="overdue-batch-nudge-all" disabled={batching || data.overall.count === 0} onClick={batchNudgeAll}
            className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
            <Send className="h-4 w-4" />{zh ? `一键催办全部 (${data.overall.count})` : `Nudge all (${data.overall.count})`}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label={zh ? "当前超期" : "Overdue"} value={data.overall.count} tint="#e11b22" />
        <KpiCard label={zh ? "今日新增" : "New today"} value={data.overall.new} tint="#dc2626" />
        <KpiCard label={zh ? "仍未处理" : "Still overdue"} value={data.overall.still} tint="#d97706" />
        <KpiCard label={zh ? "7日走势" : "7-day"} value={(() => {
          const t = (data.overall.trend7 || []).filter((v) => v != null);
          if (t.length < 2) return "—";
          const d = t[t.length - 1] - t[0];
          return d > 0 ? `↑ +${d}` : d < 0 ? `↓ ${d}` : "0";
        })()} tint={(() => { const t = (data.overall.trend7 || []).filter((v) => v != null); const d = t.length >= 2 ? t[t.length - 1] - t[0] : 0; return d > 0 ? "#e11b22" : d < 0 ? "#16a34a" : "#64748b"; })()}
          sub={zh ? "较 7 天前" : "vs 7d ago"} />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <h2 className="text-base font-semibold text-slate-800 mb-3">{zh ? "近 7 天各区域超期趋势" : "7-day overdue trend by region"}</h2>
        <div style={{ width: "100%", height: 260 }} data-testid="overdue-trend-chart">
          <ResponsiveContainer>
            <LineChart data={chart} margin={{ top: 6, right: 16, left: -12, bottom: 0 }}>
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Legend />
              {showAll && <Line type="monotone" dataKey={zh ? "全部" : "All"} stroke="#4F6BFF" strokeWidth={2.5} dot={false} />}
              {(data.regions || []).map((r) => (
                <Line key={r.region} type="monotone" dataKey={r.zh} stroke={REGION_COLOR[r.region] || "#64748b"} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {(data.regions || []).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {(data.regions || []).map((r) => (
            <div key={r.region} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`overdue-region-${r.region}`}>
              <div className="flex-wrap md:flex-nowrap flex items-center justify-between">
                <h3 className="font-semibold text-slate-800" style={ink(REGION_COLOR[r.region])}>{r.zh}</h3>
                <span className="text-2xl font-bold text-slate-900">{r.count}</span>
              </div>
              <p className="text-[12px] text-slate-500 mt-1">{zh ? `今日新增 ${r.new} · 仍在超期 ${r.still}` : `New ${r.new} · Still ${r.still}`}</p>
              <p className="text-[12px] text-slate-400 mt-0.5">{zh ? `平均超期 ${r.avgOverdue} 天` : `Avg ${r.avgOverdue}d over`}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex-wrap md:flex-nowrap flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <Users className="h-4 w-4 text-slate-400" />
          <h2 className="text-base font-semibold text-slate-800">{zh ? "销售响应率（催办后是否跟进）" : "Sales response rate"}</h2>
        </div>
        <ResponsiveTable className="w-full text-sm" data-testid="overdue-sales-table">
          <thead>
            <tr className="text-left text-slate-400 text-xs bg-slate-50">
              <th className="px-4 py-2">{zh ? "销售" : "Sales"}</th>
              <th className="px-4 py-2">{zh ? "超期数" : "Overdue"}</th>
              <th className="px-4 py-2">{zh ? "已催办" : "Nudged"}</th>
              <th className="px-4 py-2">{zh ? "已响应" : "Responded"}</th>
              <th className="px-4 py-2">{zh ? "响应率" : "Response rate"}</th>
            </tr>
          </thead>
          <tbody>
            {(data.sales || []).length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">{zh ? "暂无超期客户 ✓" : "No overdue leads ✓"}</td></tr>
            ) : (data.sales || []).map((s, i) => (
              <tr key={i} className="border-t border-slate-50" data-testid={`overdue-sales-row-${i}`}>
                <td className="px-4 py-2 font-medium text-slate-800">{s.sales}</td>
                <td className="px-4 py-2 text-slate-600">{s.overdue}</td>
                <td className="px-4 py-2 text-slate-600">{s.nudged}</td>
                <td className="px-4 py-2 text-slate-600">{s.responded}</td>
                <td className="px-4 py-2">
                  {s.responseRate == null ? <span className="text-slate-300">—</span> : (
                    <span className={`inline-flex items-center gap-1 font-semibold ${s.responseRate >= 60 ? "text-emerald-600" : s.responseRate >= 30 ? "text-amber-600" : "text-red-600"}`}>
                      {s.responseRate >= 50 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}{s.responseRate}%
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      </div>
    </div>
  );
}
